/**
 * useTopicGeneration Hook
 *
 * Custom hook for managing AI topic generation state and mutations.
 * Handles generating topics, selection state, and saving to database.
 */

import { useReducer, useCallback } from 'react';
import { useAction, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { IGeneratedTopic, ISaveTopicsResult } from '../@types/topics';
import { IBusinessProfileSnapshot } from '../@types/business';

interface TopicGenerationState {
  status: 'idle' | 'generating' | 'generated' | 'saving' | 'saved' | 'error';
  generatedTopics: IGeneratedTopic[];
  selectedTopicNames: Set<string>;
  error: string | null;
  errorCode: string | null;
  saveResult: ISaveTopicsResult | null;
}

type TopicGenerationAction =
  | { type: 'GENERATE_START' }
  | { type: 'GENERATE_SUCCESS'; payload: IGeneratedTopic[] }
  | { type: 'GENERATE_ERROR'; payload: { error: string; errorCode: string } }
  | { type: 'TOGGLE_TOPIC'; payload: string }
  | { type: 'SELECT_ALL' }
  | { type: 'DESELECT_ALL' }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_SUCCESS'; payload: ISaveTopicsResult }
  | { type: 'SAVE_ERROR'; payload: { error: string; errorCode: string } }
  | { type: 'RESET' };

const initialState: TopicGenerationState = {
  status: 'idle',
  generatedTopics: [],
  selectedTopicNames: new Set(),
  error: null,
  errorCode: null,
  saveResult: null,
};

function topicGenerationReducer(
  state: TopicGenerationState,
  action: TopicGenerationAction
): TopicGenerationState {
  switch (action.type) {
    case 'GENERATE_START':
      return {
        ...state,
        status: 'generating',
        error: null,
        errorCode: null,
        generatedTopics: [],
        selectedTopicNames: new Set(),
        saveResult: null,
      };

    case 'GENERATE_SUCCESS':
      return {
        ...state,
        status: 'generated',
        generatedTopics: action.payload,
        selectedTopicNames: new Set(), // Start with none selected
      };

    case 'GENERATE_ERROR':
      return {
        ...state,
        status: 'error',
        error: action.payload.error,
        errorCode: action.payload.errorCode,
      };

    case 'TOGGLE_TOPIC': {
      const newSelected = new Set(state.selectedTopicNames);
      if (newSelected.has(action.payload)) {
        newSelected.delete(action.payload);
      } else {
        newSelected.add(action.payload);
      }
      return {
        ...state,
        selectedTopicNames: newSelected,
      };
    }

    case 'SELECT_ALL':
      return {
        ...state,
        selectedTopicNames: new Set(state.generatedTopics.map(t => t.name)),
      };

    case 'DESELECT_ALL':
      return {
        ...state,
        selectedTopicNames: new Set(),
      };

    case 'SAVE_START':
      return {
        ...state,
        status: 'saving',
        error: null,
        errorCode: null,
      };

    case 'SAVE_SUCCESS':
      return {
        ...state,
        status: 'saved',
        saveResult: action.payload,
      };

    case 'SAVE_ERROR':
      return {
        ...state,
        status: 'error',
        error: action.payload.error,
        errorCode: action.payload.errorCode,
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

/**
 * Hook for managing topic generation workflow
 */
export const useTopicGeneration = () => {
  const [state, dispatch] = useReducer(topicGenerationReducer, initialState);

  const generateTopicsAction = useAction(api.ai.topicGeneration.generateTopics);
  const saveTopicsMutation = useMutation(api.topics.saveTopics);

  /**
   * Generate topics using AI
   */
  const generateTopics = useCallback(
    async (count: number) => {
      dispatch({ type: 'GENERATE_START' });

      try {
        const result = await generateTopicsAction({ count });

        if (!result.success) {
          dispatch({
            type: 'GENERATE_ERROR',
            payload: {
              error: result.error || 'Failed to generate topics',
              errorCode: result.errorCode || 'UNKNOWN_ERROR',
            },
          });
          return;
        }

        if (!result.topics || result.topics.length === 0) {
          dispatch({
            type: 'GENERATE_ERROR',
            payload: {
              error: 'No topics were generated',
              errorCode: 'NO_TOPICS_GENERATED',
            },
          });
          return;
        }

        dispatch({
          type: 'GENERATE_SUCCESS',
          payload: result.topics,
        });
      } catch (error) {
        dispatch({
          type: 'GENERATE_ERROR',
          payload: {
            error: error instanceof Error ? error.message : 'An unexpected error occurred',
            errorCode: 'GENERATION_FAILED',
          },
        });
      }
    },
    [generateTopicsAction]
  );

  /**
   * Toggle topic selection
   */
  const toggleTopic = useCallback((topicName: string) => {
    dispatch({ type: 'TOGGLE_TOPIC', payload: topicName });
  }, []);

  /**
   * Select all topics
   */
  const selectAll = useCallback(() => {
    dispatch({ type: 'SELECT_ALL' });
  }, []);

  /**
   * Deselect all topics
   */
  const deselectAll = useCallback(() => {
    dispatch({ type: 'DESELECT_ALL' });
  }, []);

  /**
   * Save selected topics to database
   */
  const saveSelectedTopics = useCallback(
    async (businessProfile: IBusinessProfileSnapshot): Promise<boolean> => {
      const selectedTopics = state.generatedTopics.filter(topic =>
        state.selectedTopicNames.has(topic.name)
      );

      if (selectedTopics.length === 0) {
        dispatch({
          type: 'SAVE_ERROR',
          payload: {
            error: 'Please select at least one topic to save',
            errorCode: 'NO_TOPICS_SELECTED',
          },
        });
        return false;
      }

      dispatch({ type: 'SAVE_START' });

      try {
        const result = await saveTopicsMutation({
          topics: selectedTopics,
          businessProfileSnapshot: businessProfile,
        });

        dispatch({
          type: 'SAVE_SUCCESS',
          payload: result,
        });

        return true;
      } catch (error) {
        dispatch({
          type: 'SAVE_ERROR',
          payload: {
            error: error instanceof Error ? error.message : 'Failed to save topics',
            errorCode: 'SAVE_FAILED',
          },
        });

        return false;
      }
    },
    [state.generatedTopics, state.selectedTopicNames, saveTopicsMutation]
  );

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    state,
    generateTopics,
    toggleTopic,
    selectAll,
    deselectAll,
    saveSelectedTopics,
    reset,
    hasGeneratedTopics: state.generatedTopics.length > 0,
    hasSelectedTopics: state.selectedTopicNames.size > 0,
    selectedTopicCount: state.selectedTopicNames.size,
    isGenerating: state.status === 'generating',
    isSaving: state.status === 'saving',
  };
};
